/**
 * Asking for the balance, when the work is finished.
 *
 * Jonathon, 2026-09-06: marking the last work order complete "should be an
 * email prompt that pops up for the rep that marks the work order complete so
 * they can manually review the email".
 *
 * That "manually review" is the whole design. Nothing before this in the money
 * flow needed a person — signing asks for the deposit by itself, and the
 * reminders go out on their own — but the balance is different. The crew has
 * just spent two days on somebody's roof, and whatever needs saying about how
 * that went is said by the rep who was there, not by a template that does not
 * know. So the letter is written for them, and they get the last word on it.
 *
 * The wording is `finalPaymentLetter` in packages/core, which never mentions
 * financing: that is arranged before the work, not after it.
 */

import {
  COMPANY_NAME,
  balanceLink,
  cents,
  finalPaymentLetter,
  formatMoney,
  jobAddress,
  type Job,
} from "@roof-hawk/core";

import { t } from "../../i18n";
import { useAsync } from "../../lib/hooks";
import { fetchCompanySettings, markBalanceLetterSent } from "../../lib/queries";
import { useSession } from "../../session";
import { ComposeEmail } from "../email/ComposeEmail";
import { useGmailAccount, useGmailSenders } from "../email/useGmailAccount";

export interface BalanceRequest {
  requestId: string;
  amountCents: number;
  contractCents: number;
  /**
   * Everything the customer still owes.
   *
   * The same as `amountCents` on the ordinary job. Larger when an earlier
   * payment never arrived, and then the letter says both figures rather than
   * printing the smaller one as though it were the whole debt.
   */
  outstandingCents: number;
  /** Already sent, not yet in the bank. The letter says so, and asks for less. */
  onItsWayCents?: number;
  /** The `/p/<token>` page, or null when this job never had one presented. */
  token: string | null;
}

export function BalanceRequestModal({
  job,
  request,
  onClose,
  onSent,
}: {
  job: Job;
  request: BalanceRequest;
  onClose: () => void;
  onSent?: () => Promise<void> | void;
}) {
  const { profile, displayName } = useSession();
  const { account } = useGmailAccount();
  const { senders, own: ownSender } = useGmailSenders();
  // CLAUDE.md rule 11: the name is data. `COMPANY_NAME` is only the fallback
  // for a database that has not been filled in.
  const company = useAsync(fetchCompanySettings, []);

  const contact = job.contact ?? {};
  // The estimate's own page, marked so a phone's preview of it reads "see
  // your balance" rather than "see your estimate" (linkPreview.ts).
  const link = request.token
    ? balanceLink(`${window.location.origin}/p/${request.token}`)
    : null;

  const letter = finalPaymentLetter({
    customerName: contact.full_name ?? null,
    due: cents(request.amountCents),
    outstanding: cents(request.outstandingCents),
    // Named in the letter and left out of what it asks for: a card deposit
    // sits at Stripe for two or three days (ADR 0139, Jonathon 2026-09-13).
    onItsWay: cents(request.onItsWayCents ?? 0),
    address: jobAddress(job),
    // Without a presented document there is no page to send them to. The rep
    // still gets the letter — they can attach an invoice or take it over the
    // phone — but it must not carry a link that goes nowhere.
    link: link ?? "",
    repName: profile?.full_name ?? displayName,
    // Their own mobile, under their own name, out of their own mailbox.
    repPhone: profile?.phone ?? null,
    companyName: company.data?.name ?? COMPANY_NAME,
    companyPhone: company.data?.phone ?? null,
  });

  const body = link
    ? letter.text
    : // Strip the empty line where the link would have been, rather than
      // sending a paragraph with a hole in it.
      letter.text.replace(/\n\n\n/g, "\n\n");

  return (
    <ComposeEmail
      title={t("Ask for the balance — {amount}", {
        amount: formatMoney(cents(request.amountCents)),
      })}
      subtitle={
        (link
          ? t("The work is complete. Read it over before it goes.")
          : t(
              "This job has no presented document, so there is no payment page to link to. Read it over and add how you would like to be paid.",
            )) +
        " " +
        t("Nothing is sent unless you press Send.")
      }
      to={contact.email ?? ""}
      subject={letter.subject}
      body={body}
      contact={contact}
      contactId={job.contact_id ?? undefined}
      jobId={job.id}
      connectedAs={account?.email_address ?? null}
      senders={senders}
      ownSender={ownSender}
      sendLabel={t("Send the request")}
      // Jonathon, 2026-09-10: "he needs the option to cancel the email." The
      // corner ✕ always did this, which is not the same as saying so. Nothing
      // writes to the customer afterwards either — `worthReminding` never
      // chases a balance nobody has sent, only a sent one (ADR 0260) — and
      // marking the work order complete again brings this back.
      extraActions={
        <button className="btn-ghost" onClick={onClose}>
          {t("Don’t send")}
        </button>
      }
      onClose={onClose}
      onSent={async () => {
        // Reminders count from here, so the customer is not nudged the same
        // day they were first asked.
        await markBalanceLetterSent(request.requestId);
        await onSent?.();
      }}
    />
  );
}
